<template>
  <div class="bg-base-100 rounded-lg p-6">
    <div
      class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8"
    >
      <h2 class="text-xl font-semibold">Neregistruotų pavėžėjimų sąrašas</h2>
      <div class="flex gap-2">
        <!-- DataFilter integration with reference data from store -->
        <DataFilter v-model="filters" title="Filtrai">
          <!-- Date range filters -->
          <div class="grid grid-cols-2 gap-2 mb-2">
            <fieldset class="fieldset">
              <legend class="fieldset-legend text-sm">Data nuo</legend>
              <input
                type="date"
                v-model="filters.date_from"
                class="input w-full"
              />
            </fieldset>
            <fieldset class="fieldset">
              <legend class="fieldset-legend text-sm">Data iki</legend>
              <input
                type="date"
                v-model="filters.date_to"
                class="input w-full"
              />
            </fieldset>
          </div>
        </DataFilter>

        <button
          class="btn btn-sm btn-outline"
          @click="handleExport"
          :disabled="unregisteredTripStore.isExporting"
          title="Atsisiųsti sąrašą .xlsx formatu"
        >
          <DefaultIcon name="download" size="w-4 h-4" class="mr-1" />
        </button>

        <router-link
          to="/unregistered-trips/create"
          class="btn btn-sm btn-success"
        >
          <DefaultIcon name="plus" size="w-4 h-4" />
          Naujas
        </router-link>
      </div>
    </div>

    <div v-if="unregisteredTripStore.isLoading" class="text-center py-4">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else class="overflow-x-auto">
      <div v-if="trips.length > 0">
        <table class="table w-full mb-3">
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Data</th>
              <th>Keleivių skaičius</th>
              <th>Pastaba</th>
              <th>Įtraukta</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="trip in trips" :key="trip.id">
              <td>{{ trip.id }}</td>
              <td>{{ trip.date }}</td>
              <td>{{ trip.clients_count }}</td>
              <td>{{ trip.notice }}</td>
              <td>{{ trip.created_at }}</td>
              <td>
                <div class="flex items-center gap-2">
                  <router-link
                    :to="'/unregistered-trips/' + trip.id + '/edit'"
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Peržiūrėti pavėžėjimo informacija"
                  >
                    <DefaultIcon name="editPencil" size="w-4 h-4" />
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination :meta="unregisteredTripStore.meta" />
      </div>

      <div v-else>
        <div v-if="activeFiltersCount > 0">
          Pagal pasirinktus parametrus pavėžėjimų nerasta
        </div>
        <div v-else>
          Pavėžėjimų užsakymų dar nesukūrėte. Kurkite naują pavėžėjimo užsakymą
          paspaudę - Registruoti naują.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useUnregisteredTripStore } from "@/stores/unregisteredTrips";
import { useReferenceDataStore } from "@/stores/referenceData";
import { onMounted, computed, watch, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTimeManagement } from "@/composables/time/useTimeManagement";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Pagination from "@/components/ui/pagination/PaginationComponent.vue";
import DataFilter from "@/components/filters/DataFilter.vue";

const unregisteredTripStore = useUnregisteredTripStore();
const refStore = useReferenceDataStore();

const route = useRoute();
const router = useRouter();
const { getTodayDate } = useTimeManagement();

// Extended filter values for v-model
const filters = ref({
  date_from: "",
  date_to: "",
});

// Calculate active filters count
const activeFiltersCount = computed(() => {
  return Object.values(filters.value).filter((val) => val).length;
});

const trips = computed(() => unregisteredTripStore.trips);

const loadTrips = async (query) => {
  await unregisteredTripStore.fetchTrips(query);

  // Page no longer exists (e.g. last row on the last page was deleted)
  const lastPage = unregisteredTripStore.meta?.last_page;
  if (lastPage > 0 && Number(query.page || 1) > lastPage) {
    router.replace({ query: { ...query, page: lastPage } });
  }
};

// Watch for URL changes to fetch data
watch(() => route.query, loadTrips);

onMounted(async () => {
  try {
    // Ensure reference data is loaded
    if (!refStore.isInitialized) {
      await refStore.fetchReferenceData();
    }

    // Set default parameters if none exist
    if (!route.query.page && !route.query.status) {
      await router.replace({
        query: {
          page: "1",
          date_from: getTodayDate(),
        },
      });
    }
    // Pass the initial page from route if it exists
    await loadTrips(route.query);
  } catch (error) {
    console.error("Failed to fetch travels:", error);
  }
});

const handleExport = () => {
  unregisteredTripStore.exportTrips(route.query);
};
</script>
